/**
 * Shared image-quality model.
 *
 * Clients measure cheap local metrics (blur, exposure, motion, card coverage) and
 * this module turns them into a single accept/reject decision. Keeping the policy
 * here means the extension, the iOS app and the server all reject the same frames.
 */
export const QUALITY_REJECTION_CODES = [
    'too_small',
    'too_blurry',
    'too_dark',
    'too_bright',
    'excessive_glare',
    'in_motion',
    'card_too_small',
    'occluded',
    'duplicate_frame',
    'no_card_detected',
];
/**
 * Defaults tuned for 720p-ish live-stream frames, where the card is often small,
 * moving and behind plastic. They are intentionally permissive on sharpness and
 * strict on motion, because a sharp-but-tilted card still reads and a moving one does not.
 */
export const LIVE_STREAM_THRESHOLDS = Object.freeze({
    minWidth: 224,
    minHeight: 224,
    minSharpness: 55,
    minBrightness: 32,
    maxBrightness: 232,
    maxClippedHighlightRatio: 0.18,
    maxMotionScore: 12,
    /**
     * 2% of the frame, not 4%.
     *
     * The original 4% was a guess. Swept against a 62-clip corpus of real
     * auction and break footage (126 scored frames, `npm run corpus:score --
     * --all --sweep`), F1 falls monotonically as this rises:
     *
     *     floor   precision  recall   F1
     *     0.02    0.83       0.90     0.86
     *     0.04    0.79       0.63     0.70      <- previous value
     *     0.10    0.76       0.47     0.58
     *
     * Recall is the number that matters here. Card detection is a cheap
     * pre-filter in front of an identification provider that already abstains
     * when it is unsure (DR-013), so a false positive costs one provider call
     * while a false negative is a scan the user asked for and did not get. At 4%
     * more than a third of frames containing a real card were being rejected.
     */
    minCardCoverage: 0.02,
    maxOcclusionScore: 0.45,
    duplicateHammingDistance: 6,
});
/** Stricter defaults for a phone camera, where the user controls the shot. */
export const CAMERA_THRESHOLDS = Object.freeze({
    ...LIVE_STREAM_THRESHOLDS,
    minWidth: 640,
    minHeight: 640,
    minSharpness: 120,
    maxClippedHighlightRatio: 0.12,
    maxMotionScore: 8,
    minCardCoverage: 0.15,
    maxOcclusionScore: 0.3,
});
export function assessFrame(metrics, options = {}) {
    const thresholds = options.thresholds ?? LIVE_STREAM_THRESHOLDS;
    const issues = [];
    if (metrics.width < thresholds.minWidth || metrics.height < thresholds.minHeight) {
        issues.push({
            code: 'too_small',
            message: `Frame is ${metrics.width}x${metrics.height}, below ${thresholds.minWidth}x${thresholds.minHeight}.`,
            severity: 'reject',
            measured: Math.min(metrics.width, metrics.height),
            threshold: Math.min(thresholds.minWidth, thresholds.minHeight),
            hint: 'Move closer or use a higher stream quality.',
        });
    }
    if (metrics.sharpness < thresholds.minSharpness) {
        issues.push({
            code: 'too_blurry',
            message: `Sharpness ${round2(metrics.sharpness)} below ${thresholds.minSharpness}.`,
            severity: 'reject',
            measured: round2(metrics.sharpness),
            threshold: thresholds.minSharpness,
            hint: 'Wait for the card to be held still and in focus.',
        });
    }
    if (metrics.brightness < thresholds.minBrightness) {
        issues.push({
            code: 'too_dark',
            message: `Mean brightness ${round2(metrics.brightness)} below ${thresholds.minBrightness}.`,
            severity: 'reject',
            measured: round2(metrics.brightness),
            threshold: thresholds.minBrightness,
            hint: 'Increase lighting or wait for a better-lit shot.',
        });
    }
    else if (metrics.brightness > thresholds.maxBrightness) {
        issues.push({
            code: 'too_bright',
            message: `Mean brightness ${round2(metrics.brightness)} above ${thresholds.maxBrightness}.`,
            severity: 'reject',
            measured: round2(metrics.brightness),
            threshold: thresholds.maxBrightness,
            hint: 'Reduce direct light on the card.',
        });
    }
    if (metrics.clippedHighlightRatio > thresholds.maxClippedHighlightRatio) {
        issues.push({
            code: 'excessive_glare',
            message: `${(metrics.clippedHighlightRatio * 100).toFixed(1)}% of pixels are blown out.`,
            severity: 'reject',
            measured: round2(metrics.clippedHighlightRatio),
            threshold: thresholds.maxClippedHighlightRatio,
            hint: 'Angle the card away from the light source to cut glare off the slab.',
        });
    }
    if (metrics.motionScore !== null && metrics.motionScore > thresholds.maxMotionScore) {
        issues.push({
            code: 'in_motion',
            message: `Motion score ${round2(metrics.motionScore)} above ${thresholds.maxMotionScore}.`,
            severity: 'reject',
            measured: round2(metrics.motionScore),
            threshold: thresholds.maxMotionScore,
            hint: 'Wait until the card stops moving.',
        });
    }
    if (metrics.cardCoverage !== null) {
        if (metrics.cardCoverage <= 0) {
            issues.push({
                code: 'no_card_detected',
                message: 'No card-shaped region was detected in the frame.',
                severity: 'reject',
                measured: 0,
                threshold: thresholds.minCardCoverage,
                hint: 'Point the camera at the card, or select the card area manually.',
            });
        }
        else if (metrics.cardCoverage < thresholds.minCardCoverage) {
            issues.push({
                code: 'card_too_small',
                message: `Card occupies ${(metrics.cardCoverage * 100).toFixed(1)}% of the frame.`,
                severity: 'reject',
                measured: round2(metrics.cardCoverage),
                threshold: thresholds.minCardCoverage,
                hint: 'Zoom in or crop to the card.',
            });
        }
    }
    if (metrics.occlusionScore !== null && metrics.occlusionScore > thresholds.maxOcclusionScore) {
        issues.push({
            code: 'occluded',
            message: `Occlusion score ${round2(metrics.occlusionScore)} above ${thresholds.maxOcclusionScore}.`,
            severity: 'reject',
            measured: round2(metrics.occlusionScore),
            threshold: thresholds.maxOcclusionScore,
            hint: 'Wait until fingers or other cards are not covering the card.',
        });
    }
    if (metrics.perceptualHash && options.recentHashes?.length) {
        const nearest = nearestHammingDistance(metrics.perceptualHash, options.recentHashes);
        if (nearest !== null && nearest <= thresholds.duplicateHammingDistance) {
            issues.push({
                code: 'duplicate_frame',
                message: `Frame is a near-duplicate of a recent capture (hamming ${nearest}).`,
                severity: 'reject',
                measured: nearest,
                threshold: thresholds.duplicateHammingDistance,
                hint: 'Already scanned. Waiting for the next card.',
            });
        }
    }
    const usable = !issues.some((issue) => issue.severity === 'reject');
    return { usable, score: qualityScore(metrics, thresholds, issues), issues, thresholds };
}
function qualityScore(metrics, thresholds, issues) {
    const sharpnessTerm = clamp01(Math.log10(1 + Math.max(0, metrics.sharpness)) / Math.log10(1 + thresholds.minSharpness * 8));
    const exposureTerm = 1 - clamp01(Math.abs(metrics.brightness - 128) / 128);
    const glareTerm = 1 - clamp01(metrics.clippedHighlightRatio / Math.max(0.01, thresholds.maxClippedHighlightRatio));
    const motionTerm = metrics.motionScore === null ? 0.7 : 1 - clamp01(metrics.motionScore / Math.max(1, thresholds.maxMotionScore * 2));
    const coverageTerm = metrics.cardCoverage === null ? 0.7 : clamp01(metrics.cardCoverage / 0.5);
    const raw = 0.4 * sharpnessTerm + 0.15 * exposureTerm + 0.15 * glareTerm + 0.15 * motionTerm + 0.15 * coverageTerm;
    const penalty = issues.filter((issue) => issue.severity === 'reject').length * 0.2;
    return Math.round(clamp01(raw - penalty) * 1000) / 1000;
}
/** Hamming distance between two equal-length hex hashes. Returns null on length mismatch. */
export function hammingDistance(a, b) {
    if (a.length !== b.length)
        return null;
    let distance = 0;
    for (let i = 0; i < a.length; i += 1) {
        const left = Number.parseInt(a[i], 16);
        const right = Number.parseInt(b[i], 16);
        if (Number.isNaN(left) || Number.isNaN(right))
            return null;
        distance += POPCOUNT_4BIT[left ^ right];
    }
    return distance;
}
function nearestHammingDistance(hash, others) {
    let best = null;
    for (const other of others) {
        const distance = hammingDistance(hash, other);
        if (distance === null)
            continue;
        if (best === null || distance < best)
            best = distance;
    }
    return best;
}
const POPCOUNT_4BIT = [0, 1, 1, 2, 1, 2, 2, 3, 1, 2, 2, 3, 2, 3, 3, 4];
/**
 * 64-bit difference hash from a 9x8 grayscale sample.
 * Pure function so the extension, the iOS app and the server produce identical hashes.
 */
export function differenceHash(grayscale9x8) {
    if (grayscale9x8.length !== 72)
        throw new Error('differenceHash expects exactly 72 grayscale samples (9x8)');
    let bits = '';
    for (let row = 0; row < 8; row += 1) {
        for (let col = 0; col < 8; col += 1) {
            const left = grayscale9x8[row * 9 + col];
            const right = grayscale9x8[row * 9 + col + 1];
            bits += left > right ? '1' : '0';
        }
    }
    let hex = '';
    for (let i = 0; i < 64; i += 4)
        hex += Number.parseInt(bits.slice(i, i + 4), 2).toString(16);
    return hex;
}
function clamp01(value) {
    if (Number.isNaN(value))
        return 0;
    return Math.min(1, Math.max(0, value));
}
function round2(value) {
    return Math.round(value * 100) / 100;
}
//# sourceMappingURL=assess.js.map